// Batch issue entry — spreadsheet-style table posting to
// POST /issue/bulk. One row per issue: Title • Description • Plan item
// • Discipline • Area • Level • Grid • Priority • Due • Responsible.
// Description is the issue's opening note (what it is, who should act),
// the same field the single-issue form calls Description; Responsible is
// picked from the project roster - the owner company's own people and
// every partner company's members and account-less contacts - and an
// address may still be typed to invite someone new. A row can name
// SEVERAL responsibles: they all land in the issue's responsibleUsers[]
// and all get the reminders, exactly like the single-issue form.
// CSV paste (papaparse) appends rows: WITH a header row columns are
// matched by name (so Description can appear anywhere and older sheets
// without it still work); without one, the legacy fixed order above
// minus Description applies. Several responsibles in one cell are
// separated by a comma, a semicolon or a space. Unknown responsible
// emails become
// ResponsibleInvites (one email per address); known users get one
// digest notification.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert, Button, Checkbox, Empty, Input, InputNumber, Popconfirm, Select, Table, Tag, Tooltip, message,
} from "antd";
import { DeleteOutlined, PlusOutlined, SendOutlined, PictureOutlined, CloseCircleFilled } from "@ant-design/icons";
import Papa from "papaparse";
import { getIssueTags } from "api/issue/issueApi";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import useProjectAccess from "hooks/useProjectAccess";
import { getParticipantsRoster } from "api/participants/participantsApi";
import { peoplePickerOptions, filterPeopleOption, renderPeopleOption, peopleTagPlaceholder } from "components/PeoplePicker/PeoplePicker";
import { getDiscipline } from "api/discipline/getDiscipline";
import DisciplineContractorPicker from "components/Issue/DisciplineContractorPicker";
import { useSelector } from "react-redux";
import LocationPicker from "components/Coordination/LocationPicker";
import { shouldInterceptPaste } from "helpers/pasteTarget";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { leafActivityOptions, LEAF_SELECT_PROPS } from "helpers/schedule/leafActivityOptions";
import ResizableTitle from "components/Table/ResizableTitle";
import { matchOption } from "components/SearchSelect/searchSelect";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

const PRIORITIES = ["urgent", "normal", "low"];
const emptyRow = () => ({
  key: Math.random().toString(36).slice(2),
  // ganttItemIds: one or several activities, all equal (owner, 2026-10-17).
  title: "", description: "", ganttItemIds: [], discipline: "", area: "", level: "",
  // Required on every row (owner, 2026-10-16); POST /issue/bulk refuses a
  // batch with a row missing it.
  estimatedHours: null,
  grid: "", system: "", priority: "normal", dueDate: "", responsibleEmails: [],
  // The row's tags (owner, 2026-10-15: "issues, can be tagged by multiple
  // tags"); the first goes on the title ("SNAG" -> "SNAG-<title>").
  tags: [],
  // The contractor picked under its discipline ("" = none picked).
  responsibleCompanyId: "",
  // The screenshot for THIS issue: { file, dataUrl, name }. Held on the
  // row until the issues exist, then uploaded to the one it belongs to.
  image: null,
});
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A responsible as a chip: the person's name, and on hover their email,
// company and whether they are being invited. Shared by the row cell and the
// fill panel so the two read the same.
function personTagRender(describePerson) {
  return function PersonTag({ value: email, closable, onClose }) {
    const d = describePerson(email);
    return (
      <Tooltip placement="top" title={<span><b>{d.title}</b><br /><span style={{ fontSize: 11 }}>{d.sub}</span></span>}>
        <Tag closable={closable} onClose={onClose}
             onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
             style={{ marginInlineEnd: 4, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>
          {d.title}
        </Tag>
      </Tooltip>
    );
  };
}

// RESIZABLE COLUMNS (owner, 2026-10-14): components/Table/ResizableTitle.

const WIDTHS_KEY = "batchIssueColumnWidths";
function savedWidths() {
  try { return JSON.parse(localStorage.getItem(WIDTHS_KEY) || "{}") || {}; } catch (e) { return {}; }
}

// ONE PANEL, ONE SET OF BUTTONS: every column's value (responsibles and the
// tag included) and Apply to selected / Apply to all rows / Fill empty rows /
// Reset. A field left blank is left alone on every row.
function FillPanel({
  fill, setFill, reset, applyFill, selectedCount, projectId,
  responsibleGroups, rosterLoading, describePerson, tagOptions, planOptions, companyDisciplines, roster,
}) {
  const set = (patch) => setFill((f) => ({ ...f, ...patch }));
  const label = (t) => <span style={{ fontSize: 11, color: "#6B7280" }}>{t}</span>;
  const cell = (t, el, w) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, width: w }}>{label(t)}{el}</div>
  );
  return (
    <div data-testid="fill-panel"
         style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", marginBottom: 10, background: "#fafafa", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <b>Fill columns</b>
        <span style={{ color: "#6B7280", marginLeft: 8 }}>
          Set any of these, then apply them. A field left blank is left alone.
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        {cell("Responsible", (
          <Select
            size="small" allowClear showSearch mode="tags" tokenSeparators={[",", ";", " "]} maxTagCount="responsive"
            value={fill.responsibleEmails} options={responsibleGroups} maxTagPlaceholder={peopleTagPlaceholder(responsibleGroups)}
            filterOption={filterPeopleOption} optionRender={renderPeopleOption}
            placeholder={rosterLoading ? "loading people…" : "people, or type emails"}
            notFoundContent={rosterLoading ? "loading…" : "No match - type a full email to invite"}
            tagRender={personTagRender(describePerson)}
            onChange={(vals) => set({ responsibleEmails: (vals || []).map((x) => String(x).trim().toLowerCase()).filter(Boolean) })}
          />
        ), 300)}
        {cell("Tags", (
          <Select
            size="small" mode="tags" allowClear maxTagCount="responsive" tokenSeparators={[","]}
            value={fill.tags} options={tagOptions} aria-label="Fill tags"
            onChange={(v) => set({ tags: (v || []).map((t) => String(t).trim().toUpperCase()).filter(Boolean) })}
            placeholder="e.g. SNAG, RFI-7"
            notFoundContent={projectId ? "No tags on this project yet - type to start one" : "Pick a project to see its tags"}
          />
        ), 200)}
        {cell("Activities", (
          <Select filterOption={matchOption} size="small" mode="multiple" allowClear maxTagCount="responsive" {...LEAF_SELECT_PROPS} placeholder="Activities" dropdownMatchSelectWidth={360}
                  aria-label="Fill activities"
                  value={fill.ganttItemIds} options={planOptions} onChange={(v) => set({ ganttItemIds: v || [] })} />
        ), 200)}
        {cell("Discipline / contractor", (
          <DisciplineContractorPicker size="small" placeholder="Discipline" aria-label="Fill discipline"
            disciplines={companyDisciplines} roster={roster}
            value={fill.discipline ? { discipline: fill.discipline, responsibleCompanyId: fill.responsibleCompanyId } : null}
            onChange={(pick) => set({ discipline: pick ? pick.discipline : undefined, responsibleCompanyId: pick && pick.responsibleCompanyId ? pick.responsibleCompanyId : "" })} />
        ), 220)}
        {cell("Level · Area · Grid · System", (
          <LocationPicker projectId={projectId} size="small" fill
                          value={{ level: fill.level, area: fill.area, grid: fill.grid, system: fill.system }}
                          onChange={(loc) => set({ level: loc.level || "", area: loc.area || "", grid: loc.grid || "", system: loc.system || "" })} />
        ), 480)}
        {cell("Priority", (
          <Select size="small" allowClear placeholder="Priority"
                  value={fill.priority} options={PRIORITIES.map((pr) => ({ value: pr, label: pr }))} onChange={(v) => set({ priority: v })} />
        ), 100)}
        {cell("Est. hours", (
          <InputNumber size="small" aria-label="Fill estimated hours" min={0.25} step={0.25} precision={2}
                       value={fill.estimatedHours} placeholder="hours" style={{ width: "100%" }}
                       onChange={(v) => set({ estimatedHours: v })} />
        ), 90)}
        {cell("Due", (
          <input type="date" value={fill.dueDate} title="Due"
                 style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "2px 6px", fontSize: 12 }}
                 onChange={(e) => set({ dueDate: e.target.value })} />
        ), 130)}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <Button size="small" type="primary" disabled={!selectedCount} onClick={() => applyFill("selected")}
                title="Put these values on the ticked rows">
          Apply to selected ({selectedCount})
        </Button>
        <Button size="small" onClick={() => applyFill("all")}>Apply to all rows</Button>
        <Button size="small" onClick={() => applyFill("empty")}
                title="Only cells that are still empty (priority: still the default)">
          Fill empty rows
        </Button>
        <Button size="small" onClick={reset}>Reset</Button>
      </div>
    </div>
  );
}

export default function BatchIssueEntryPage() {
  const history = useHistory();
  const userInfo = useSelector((st) => st.user.userInfo);
  const [projects, setProjects] = useState([]);
  // Shared coordination memory: ?projectId= wins, else the last project
  // picked anywhere in the suite, else the first project (below).
  const [projectId, setProjectId] = useLastProject(projects);
  // issueCreate on this project (POST /issue/bulk is gated on it). Until the
  // answer arrives the button stays as it was; the server checks either way.
  const { can: projectCan, access: projectAccess } = useProjectAccess(projectId);
  const mayCreate = !projectAccess || !!projectCan.issueCreate;
  const [planItems, setPlanItems] = useState([]);
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);
  // Everyone assignable on this project: the owner company's own people
  // plus every partner company's members and account-less contacts.
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  // The company's own trade list (models/discipline.js). The partners'
  // trades ride along on the roster, so between the two the Discipline
  // cell can offer what this project actually works in.
  const [companyDisciplines, setCompanyDisciplines] = useState([]);
  // ONE FILL PANEL (owner, 2026-10-14: "merge responsible input, tag with
  // fill columns and make sure 1 set of buttons has apply to selected, apply
  // to all rows, fill empty rows, and reset"). One value per column -
  // responsibles and the tag included - put onto the ticked rows, every row,
  // or only the cells still empty. A column left blank here is left alone on
  // every row - the same rule as the manager's "Set fields".
  const [selectedKeys, setSelectedKeys] = useState([]);
  const emptyFill = { responsibleEmails: [], tags: [], ganttItemIds: [], discipline: undefined, responsibleCompanyId: "", area: "", level: "", grid: "", system: "", priority: undefined, dueDate: "", estimatedHours: null };
  const [fill, setFill] = useState(emptyFill);
  const fillValues = () => {
    const out = {};
    if ((fill.responsibleEmails || []).length) out.responsibleEmails = [...fill.responsibleEmails];
    if ((fill.tags || []).length) out.tags = [...fill.tags];
    if ((fill.ganttItemIds || []).length) out.ganttItemIds = [...fill.ganttItemIds];
    if (fill.discipline) out.discipline = fill.discipline;
    // A contractor goes with the discipline it was picked under; a bare
    // discipline leaves each row's contractor as it is.
    if (fill.discipline && fill.responsibleCompanyId) out.responsibleCompanyId = fill.responsibleCompanyId;
    for (const k of ["area", "level", "grid", "system"]) if (String(fill[k] || "").trim()) out[k] = String(fill[k]).trim();
    if (fill.priority) out.priority = fill.priority;
    if (fill.dueDate) out.dueDate = fill.dueDate;
    if (Number(fill.estimatedHours) > 0) out.estimatedHours = Number(fill.estimatedHours);
    return out;
  };
  const applyFill = (scope) => {
    const vals = fillValues();
    const keys = Object.keys(vals);
    if (!keys.length) { message.warning("Pick at least one value to fill"); return; }
    let touched = 0;
    setRows((rs) => rs.map((r) => {
      if (scope === "selected" && !selectedKeys.includes(r.key)) return r;
      const next = { ...r };
      let changed = false;
      for (const k of keys) {
        // "empty" fills only cells with nothing in them; priority always
        // has a value, so there it means "still the default".
        const blank = k === "priority" ? r.priority === "normal"
          : Array.isArray(r[k]) ? !r[k].length : !String(r[k] || "").trim();
        if (scope === "empty" && !blank) continue;
        const same = Array.isArray(vals[k]) ? JSON.stringify(next[k] || []) === JSON.stringify(vals[k]) : next[k] === vals[k];
        if (!same) { next[k] = Array.isArray(vals[k]) ? [...vals[k]] : vals[k]; changed = true; }
      }
      if (changed) touched += 1;
      return changed ? next : r;
    }));
    if (scope === "selected" && !selectedKeys.length) { message.warning("Tick the rows to fill first"); return; }
    message.success(touched ? `Filled ${keys.length} column${keys.length === 1 ? "" : "s"} on ${touched} row${touched === 1 ? "" : "s"}` : "Nothing to change");
  };
  const [result, setResult] = useState(null);
  // Tags already used on this project, offered by the fill panel and the Tag column.
  const [tagOptions, setTagOptions] = useState([]);

  useEffect(() => {
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((ps) => {
        // Picker responds { projects: [...] }; tolerate a bare array too.
        const list = Array.isArray(ps) ? ps : (ps?.projects || []);
        setProjects(list);
        if ((!projectId || !list.some((p) => p._id === projectId)) && list[0]?._id) setProjectId(list[0]._id);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!projectId) return;
    fetch(`${url}/projects/${projectId}/gantt`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((feed) =>
        setPlanItems(
          // Finished and disabled activities take no new work: a
          // disabled row was called off, and the server refuses issues
          // raised on one.
          // The whole schedule: a finished row is still a parent, and only
          // live LEAVES are offered (planOptions below).
          withActivityPaths(feed.items || [])
        )
      )
      .catch(() => setPlanItems([]));
  }, [projectId]);

  // Tags already used on this project, so a batch joins an existing set
  // rather than inventing a near-miss spelling of it.
  useEffect(() => {
    let cancelled = false;
    // Project-bound, like the single form: a batch joins THIS project's
    // tags or starts a new one, never another project's.
    setFill((f) => ({ ...f, tags: [], responsibleCompanyId: "" }));   // the old project's labels and partners do not follow
    if (!projectId) { setTagOptions([]); return undefined; }
    getIssueTags(projectId)
      .then((r) => {
        if (cancelled) return;
        setTagOptions((r?.tags || []).map((t) => ({
          value: t.tag,
          label: `${t.tag}  ·  ${t.count} issue${t.count === 1 ? "" : "s"}`,
        })));
      })
      .catch(() => { if (!cancelled) setTagOptions([]); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) { setRoster([]); return; }
    let alive = true;
    setRosterLoading(true);
    getParticipantsRoster(projectId)
      .then((data) => { if (alive) setRoster(Array.isArray(data) ? data : (data?.roster || [])); })
      .catch(() => { if (alive) setRoster([]); })
      .finally(() => { if (alive) setRosterLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    const company = userInfo?.company;
    if (!company) { setCompanyDisciplines([]); return; }
    let alive = true;
    getDiscipline(company)
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data?.data || []);
        setCompanyDisciplines(list.filter((d) => d && !d.deleted));
      })
      .catch(() => { if (alive) setCompanyDisciplines([]); });
    return () => { alive = false; };
  }, [userInfo?.company]);

  const setCell = (key, field, value) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  // One write for the whole location: picking an area can move the level.
  const setLoc = (key, loc) => setRows((rs) => rs.map((r) => (r.key === key
    ? { ...r, level: loc.level || "", area: loc.area || "", grid: loc.grid || "", system: loc.system || "" }
    : r)));

  // Column widths, dragged by the header edge; remembered in this browser.
  const [widths, setWidths] = useState(savedWidths);
  useEffect(() => {
    try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(widths)); } catch (e) { /* per-browser nicety only */ }
  }, [widths]);

  // WHICH ROW IS THE PASTE FOR.
  //
  // A screenshot on the clipboard has no idea which issue it is about,
  // and getting it wrong is worse than not supporting it at all: the
  // wrong photo attached to the wrong defect is a false record that
  // somebody acts on. So the row is decided by where the person IS, in
  // this order:
  //
  //   1. the caret - if a field in a row has focus, that row is meant,
  //      whatever the mouse is doing;
  //   2. the mouse - the row under the pointer, which is where somebody
  //      who clicked nothing and pressed Ctrl+V is looking.
  //
  // Neither is a guess: both are recorded from real events, and when
  // there is no answer the paste is refused with a message rather than
  // landing somewhere plausible. The armed row is highlighted so it is
  // visible BEFORE the paste, not discovered after it.
  const [hoverRowKey, setHoverRowKey] = useState("");
  // The document listener outlives individual renders; these keep it
  // reading today's rows rather than the ones it closed over.
  const rowsRef = useRef([]);
  const onPasteRef = useRef(() => {});
  const [focusRowKey, setFocusRowKey] = useState("");
  const armedRowKey = focusRowKey || hoverRowKey;
  // READ THROUGH REFS, NOT THE LISTENER'S CLOSURE (owner, 2026-10-14: "image
  // paste for each row fails sometime"). The row clicked to arm it was only
  // ever highlighted: the paste itself ignored it and fell back to wherever
  // the pointer was, so moving the mouse off the row - to the toolbar, or
  // over the image preview - after clicking it refused the paste or put the
  // picture on the row under the pointer.
  const focusRowRef = useRef("");
  const hoverRowRef = useRef("");
  focusRowRef.current = focusRowKey;
  hoverRowRef.current = hoverRowKey;

  const rowKeyFromEvent = (e) => {
    const live = new Set((rowsRef.current || []).map((r) => r.key));
    const ok = (k) => (k && live.has(k) ? k : "");
    // The caret wins. document.activeElement is inside the <tr> whose
    // data-row-key antd stamps on every row.
    try {
      const el = document.activeElement;
      const tr = el && el.closest ? el.closest("tr[data-row-key]") : null;
      if (tr && ok(tr.getAttribute("data-row-key"))) return tr.getAttribute("data-row-key");
    } catch (err) { /* no DOM answer, fall through */ }
    // Then the row clicked to arm it - what the blue highlight promises.
    if (ok(focusRowRef.current)) return focusRowRef.current;
    // Then the pointer, from the paste event when the browser gives one,
    // and from the last hover when it does not.
    try {
      const tr = e?.target?.closest ? e.target.closest("tr[data-row-key]") : null;
      if (tr && ok(tr.getAttribute("data-row-key"))) return tr.getAttribute("data-row-key");
    } catch (err) { /* fall through */ }
    return ok(hoverRowRef.current);
  };

  // Items first; some browsers (and some snipping tools) put the picture
  // only in clipboardData.files.
  const imageFromClipboard = (e) => {
    const cd = e.clipboardData;
    if (!cd) return null;
    const items = Array.from(cd.items || []);
    const it = items.find((x) => x.kind === "file" && /^image\//.test(x.type || ""));
    const f = it ? it.getAsFile() : null;
    if (f) return f;
    return Array.from(cd.files || []).find((x) => /^image\//.test(x.type || "")) || null;
  };

  const attachImageToRow = (rowKey, file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setRows((rs) => rs.map((r) => (r.key === rowKey
        ? { ...r, image: { file, dataUrl: String(reader.result || ""), name: file.name || "pasted.png" } }
        : r)));
    };
    reader.readAsDataURL(file);
  };

  const onPaste = (text) => {
    const parsed = Papa.parse(String(text || "").trim(), { skipEmptyLines: true });
    const data = parsed?.data || [];
    if (!data.length) return;
    const header = data[0].map((c) => String(c).toLowerCase().trim());
    const hasHeader = header.includes("title");
    const body = hasHeader ? data.slice(1) : data;
    // With a header row, columns are matched BY NAME - so a Description
    // column can sit anywhere, and existing sheets without one still
    // work. Without a header, the legacy fixed order is kept so older
    // paste habits are not broken.
    const LEGACY = ["title", "activity", "discipline", "area", "level", "grid", "priority", "due", "responsible"];
    const idxOf = (names) => {
      if (!hasHeader) return LEGACY.findIndex((n) => names.includes(n));
      for (const n of names) {
        const i = header.findIndex((h) => h === n || h.startsWith(n));
        if (i >= 0) return i;
      }
      return -1;
    };
    const col = {
      title: idxOf(["title"]),
      description: idxOf(["description", "comment", "note", "details"]),
      activity: idxOf(["activity", "plan item", "schedule"]),
      discipline: idxOf(["discipline", "trade"]),
      area: idxOf(["area"]), level: idxOf(["level"]), grid: idxOf(["grid"]),
      // Header-only (the legacy fixed order never had them).
      system: hasHeader ? idxOf(["system"]) : -1,
      tag: hasHeader ? idxOf(["tag"]) : -1,
      estimate: hasHeader ? idxOf(["estimated hours", "estimate", "est. hours", "est hours", "hours"]) : -1,
      priority: idxOf(["priority"]), due: idxOf(["due", "due date"]),
      responsible: idxOf(["responsible", "responsible email", "assignee", "email"]),
    };
    const at = (cols, i) => (i >= 0 ? String(cols[i] || "").trim() : "");
    const added = body
      .filter((cols) => at(cols, col.title))
      .map((cols) => ({
        ...emptyRow(),
        title: at(cols, col.title),
        description: at(cols, col.description),
        // Plan items matched by name (case-insensitive) since CSV
        // carries names, not ids; several are separated by ";".
        ganttItemIds: [...new Set(at(cols, col.activity).split(";").map((n) => n.trim().toLowerCase()).filter(Boolean)
          .map((n) => planItems.find((it) => it.name?.toLowerCase() === n)?._id).filter(Boolean))],
        discipline: at(cols, col.discipline),
        area: at(cols, col.area),
        level: at(cols, col.level),
        grid: at(cols, col.grid),
        system: at(cols, col.system),
        // Several tags in one cell: "SNAG; RFI-7" or "SNAG, RFI-7".
        tags: at(cols, col.tag).split(/[;,|]/).map((t) => t.trim().toUpperCase()).filter(Boolean)
          .filter((t, i, all) => all.indexOf(t) === i),
        priority: PRIORITIES.includes(at(cols, col.priority).toLowerCase())
          ? at(cols, col.priority).toLowerCase()
          : "normal",
        dueDate: at(cols, col.due),
        estimatedHours: Number(at(cols, col.estimate)) > 0 ? Number(at(cols, col.estimate)) : null,
        // One cell, one or more people: "a@x.com; b@x.com".
        responsibleEmails: at(cols, col.responsible)
          .toLowerCase()
          .split(/[;,\s]+/)
          .map((e) => e.trim())
          .filter(Boolean),
      }));
    if (added.length) {
      setRows((rs) => [...rs.filter((r) => r.title.trim()), ...added]);
      message.success(`${added.length} row(s) pasted`);
    }
  };

  // Proof required before any of these issues can be marked resolved
  // (same policy as the single-issue form; enforced server-side).
  const [proof, setProof] = useState(["screenshot"]);
  // ONE TAG FOR THE WHOLE BATCH.
  //
  // That is the normal case this page exists for - a floor walk, an RFI
  // round, one visit - and it is what makes the batch retrievable later:
  // every row gets "TAG-" in front of its title and the list can filter
  // the lot back out. Offered as a picker over the tags already in use so
  // a batch is not stranded under a misspelling of last week's tag.
  // Since 2026-10-14 the tag is a fill-panel value like the others, held on
  // each row (row.tags, several since 2026-10-15) - the server takes tags per row; tagOptions (the
  // project's tags in use) is declared at the top, before the effect that
  // loads it.
  const submit = async () => {
    // Caught here rather than as a per-row server error: a typo'd address
    // would otherwise throw away the whole row.
    const badEmail = rows
      .filter((r) => r.title.trim())
      .flatMap((r) => r.responsibleEmails || [])
      .find((e) => !EMAIL_RE.test(e));
    if (badEmail) return message.warning(`"${badEmail}" is not an email address`);
    // Every row needs its estimated hours - named by row, like the server
    // does, so the person knows which cells to fill.
    const noEstimate = rows
      .filter((r) => r.title.trim())
      .map((r, i) => (Number(r.estimatedHours) > 0 ? 0 : i + 1))
      .filter(Boolean);
    if (noEstimate.length) {
      return message.warning(`Enter the estimated hours on row${noEstimate.length === 1 ? "" : "s"} ${noEstimate.join(", ")}`);
    }
    const payloadRows = rows
      .filter((r) => r.title.trim())
      .map((r) => ({
        title: r.title,
        estimatedHours: Number(r.estimatedHours),
        // Becomes the issue's opening note - what the issue is and who
        // should act - exactly like Description on the single-issue form.
        description: r.description || undefined,
        ganttItemIds: (r.ganttItemIds || []).length ? r.ganttItemIds : undefined,
        discipline: r.discipline, area: r.area, level: r.level, grid: r.grid,
        // Kept on the row since 2026-10-14 - the picker offered it before but
        // the row never held it, so a pick snapped back to empty.
        system: r.system || undefined,
        // Always sent, empty included: the server falls back to a batch tag
        // only when a row's is undefined, and there is no batch tag any more.
        // The first tag is the one on the title; the server keeps them all.
        titleTag: String((r.tags || [])[0] || "").trim().toUpperCase(),
        tags: (r.tags || []).map((t) => String(t).trim().toUpperCase()).filter(Boolean),
        // Picked under its discipline; validated server-side against the
        // project's active partners.
        responsibleCompanyId: r.responsibleCompanyId || undefined,
        priority: r.priority, dueDate: r.dueDate || undefined,
        // Everyone named on the row; the server assigns them all and
        // invites the addresses it does not know.
        responsibleEmails: r.responsibleEmails?.length ? r.responsibleEmails : undefined,
      }));
    if (!payloadRows.length) return message.warning("Add at least one row with a title");
    setSaving(true);
    setResult(null);
    try {
      const res = await fetch(`${url}/issue/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ projectId, rows: payloadRows, evidencePolicy: { screenshot: proof.includes("screenshot"), document: proof.includes("document"), modelPush: proof.includes("modelPush"), pluginResolve: proof.includes("pluginResolve") } }),
      });
      const body = await res.json();
      if (!res.ok && !body.created?.length) throw new Error(body.err || `HTTP ${res.status}`);
      setResult(body);
      if (body.created?.length) {
        // EACH PICTURE FOLLOWS ITS OWN ISSUE.
        //
        // The server answers with the ROW INDEX each issue came from
        // (created[].row, indexed into the rows that had a title - the
        // same list that was posted), which is what makes this safe: the
        // image is matched to the issue by the row it was pasted on, not
        // by position in a list that filtering has already changed.
        const posted = rows.filter((r) => r.title.trim());
        const withImages = body.created.filter((c) => posted[c.row]?.image?.file);
        let attached = 0;
        const failed = [];
        for (const c of withImages) {
          const img = posted[c.row].image;
          try {
            const fd = new FormData();
            fd.append("files", img.file, img.name || "pasted.png");
            const up = await fetch(`${url}/issue/${c._id}/attachments`, {
              method: "POST", headers: auth(), body: fd,
            });
            if (!up.ok) throw new Error(`HTTP ${up.status}`);
            attached += 1;
          } catch (e) {
            failed.push(c.displayId || c.title || String(c._id));
          }
        }
        if (attached) message.success(`${attached} image(s) attached`);
        if (failed.length) {
          message.warning(`The issue was created but its image did not attach: ${failed.join(", ")}`);
        }
        message.success(`${body.created.length} issue(s) created`);
        // Keep only rows that failed so the user can fix them.
        const failedIdx = new Set((body.errors || []).map((e) => e.row));
        setRows((rs) => {
          const withTitle = rs.filter((r) => r.title.trim());
          const kept = withTitle.filter((_, i) => failedIdx.has(i));
          return kept.length ? kept : [emptyRow()];
        });
      }
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Grouped by company so it is obvious who works for whom. The VALUE is
  // the email: POST /issue/bulk resolves a known address to its account
  // and invites an unknown one, so contacts without a login work too.
  // The shared picker's options (components/PeoplePicker, owner
  // 2026-10-15): each partner under its trade, people tagged by kind; a
  // partner executive is listed greyed out - a reader, never responsible.
  const responsibleGroups = useMemo(
    () => peoplePickerOptions(roster, { personValue: (p) => p.email, executives: "disable" }),
    [roster]
  );

  // WHAT DISCIPLINES THIS PROJECT ACTUALLY WORKS IN, AND WHO DOES EACH.
  //
  // Two sources, because the answer lives in two places: the owner
  // company's own trade list (models/discipline.js, the same list Doc
  // Control and the analytics rollups use), and the trades each partner
  // company was brought onto the project to do. Since 2026-10-15 (owner:
  // "the dicipline, should list each contractor by discipline") both the
  // fill panel and the Discipline column use the shared picker
  // (components/Issue/DisciplineContractorPicker): a group per discipline,
  // the partners doing it under it, and a value nobody offered kept visible
  // as "typed" rather than blanked.

  // email -> who that is, for the hover card on a responsible tag. A
  // typed-in address that is not on the project has no entry and is
  // described as an invite, which is what the server will do with it.
  const personByEmail = useMemo(() => {
    const m = new Map();
    for (const co of roster || []) {
      for (const u of co.users || []) {
        if (!u.email) continue;
        m.set(String(u.email).toLowerCase(), {
          name: u.name || "",
          email: u.email,
          company: co.companyName || "",
          contact: !!u.contact,
        });
      }
    }
    return m;
  }, [roster]);

  // One responsible, described. Used by both the per-tag hover and the
  // "+N" overflow hover.
  const describePerson = (email) => {
    const p = personByEmail.get(String(email).toLowerCase());
    if (!p) return { title: email, sub: "not on this project - will be invited" };
    return {
      title: p.name ? `${p.name}` : p.email,
      sub: [p.email, p.company, p.contact ? "no account - invited" : ""].filter(Boolean).join(" · "),
    };
  };

  const planOptions = useMemo(
    // THE LOWEST ACTIVITY ONLY, its path beneath (owner, 2026-10-15).
    () => leafActivityOptions(planItems, { offer: (it) => it.status !== "done" && !it.disabled }),
    [planItems]
  );

  // THE LISTENER HAS TO BE ON THE DOCUMENT.
  //
  // A paste is delivered to whatever has focus, and when nothing does -
  // somebody moves the mouse over a row and presses Ctrl+V - the browser
  // targets document.body, which is OUTSIDE this page's markup. A
  // handler on the wrapper therefore never sees exactly the case the
  // mouse targeting exists for. Verified in a browser, not assumed: with
  // the handler on the div, the hover paste silently did nothing.
  rowsRef.current = rows;
  onPasteRef.current = onPaste;

  useEffect(() => {
    const onDocPaste = (e) => {
      const img = imageFromClipboard(e);
      if (img) {
        // The image belongs to ONE row, and pasting it must not also
        // drop its filename into whatever field had the caret.
        const key = rowKeyFromEvent(e);
        e.preventDefault();
        if (!key) {
          message.warning("Hover the row you want the image on, or click into it, then paste");
          return;
        }
        attachImageToRow(key, img);
        const n = rowsRef.current.findIndex((r) => r.key === key);
        message.success(`Image attached to row ${n >= 0 ? n + 1 : ""}`.trim());
        return;
      }
      // Only intercept multi-row pastes so normal single-field pasting
      // still works - and "multi-row" means WHERE IT WAS AIMED as well as
      // what it contains.
      //
      // This used to test the text alone: anything with a newline was
      // taken as a spreadsheet. A newline does not mean "table". A
      // description with a line break has one too, and pasting one into
      // the Description box had its paste CANCELLED and its text fed to
      // the CSV parser, so it never arrived (owner, 2026-09-24). The
      // comment here promised exactly the behaviour it prevented.
      const text = e.clipboardData?.getData("text") || "";
      if (shouldInterceptPaste(text, e.target)) {
        e.preventDefault();
        onPasteRef.current(text);
      }
    };
    document.addEventListener("paste", onDocPaste);
    return () => document.removeEventListener("paste", onDocPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      // The picture leads the row, where a thumbnail is worth more than
      // a column heading: you can see at a glance which entries have
      // their evidence and which are still bare.
      title: "Image", dataIndex: "image", width: 92,
      render: (img, r) => (img ? (
        <div style={{ position: "relative", width: 72 }}>
          {/* The thumbnail is 72x48 and cropped to fill, which is enough
              to see THAT a row has its evidence and nowhere near enough
              to see WHAT it is. Hovering gives the whole frame, uncropped
              and big enough to read, so a screenshot pasted into the
              wrong row is caught here rather than after posting. */}
          <Tooltip
            placement="right"
            overlayInnerStyle={{ padding: 6, background: "#fff" }}
            title={(
              <div style={{ maxWidth: 620 }}>
                <img
                  src={img.dataUrl}
                  alt={img.name || "pasted"}
                  style={{ display: "block", maxWidth: 600, maxHeight: 460, objectFit: "contain", borderRadius: 4 }}
                />
                <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                  {img.name || "pasted image"}
                </div>
              </div>
            )}
          >
            <img
              src={img.dataUrl}
              alt={img.name || "pasted"}
              style={{ width: 72, height: 48, objectFit: "cover", borderRadius: 4, border: "1px solid #d9d9d9", display: "block", cursor: "zoom-in" }}
            />
          </Tooltip>
          <CloseCircleFilled
            title="Remove this image"
            onClick={() => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, image: null } : x)))}
            style={{ position: "absolute", top: -6, right: -6, color: "#d64545", background: "#fff", borderRadius: "50%", cursor: "pointer", fontSize: 15 }}
          />
        </div>
      ) : (
        <div
          onClick={() => setFocusRowKey(r.key)}
          title="Click this row, then paste a screenshot"
          style={{
            width: 72, height: 48, borderRadius: 4, cursor: "pointer",
            border: `1px dashed ${r.key === armedRowKey ? "#2F6FED" : "#d9d9d9"}`,
            color: r.key === armedRowKey ? "#2F6FED" : "#bfbfbf",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
          }}
        >
          <PictureOutlined />
        </div>
      )),
    },
    {
      title: "Title *", dataIndex: "title", width: 220,
      render: (v, r) => (
        <Input size="small" value={v} placeholder="Issue title"
               onChange={(e) => setCell(r.key, "title", e.target.value)} />
      ),
    },
    {
      // The tag on the front of the title ("SNAG" -> "SNAG-<title>"), per
      // row; the fill panel sets it on many rows at once.
      title: "Tags", dataIndex: "tags", width: 160,
      render: (v, r) => (
        <Select size="small" mode="tags" style={{ width: "100%" }} value={v || []} options={tagOptions} allowClear
                maxTagCount="responsive" tokenSeparators={[","]} placeholder="—" aria-label="Row tags"
                onChange={(val) => setCell(r.key, "tags", (val || []).map((t) => String(t).trim().toUpperCase()).filter(Boolean))} />
      ),
    },
    {
      title: "Description", dataIndex: "description", width: 300,
      render: (v, r) => (
        <Input.TextArea
          size="small" value={v} autoSize={{ minRows: 1, maxRows: 8 }} style={{ width: "100%" }}
          placeholder="What the issue is and who needs to act on it"
          onChange={(e) => setCell(r.key, "description", e.target.value)}
        />
      ),
    },
    {
      title: "Activities", dataIndex: "ganttItemIds", width: 200,
      render: (v, r) => (
        <Select filterOption={matchOption} size="small" mode="multiple" allowClear maxTagCount="responsive" {...LEAF_SELECT_PROPS} dropdownMatchSelectWidth={360}
                style={{ width: "100%" }} value={v || []} aria-label="Row activities"
                options={planOptions} placeholder="—"
                onChange={(val) => setCell(r.key, "ganttItemIds", val || [])} />
      ),
    },
    {
      // A dropdown, not free text - but one that never loses a value it
      // did not offer (a CSV paste, an older row): the picker lists it as
      // "typed". Picking a contractor under a discipline sets both on the row.
      title: "Discipline / contractor", dataIndex: "discipline", width: 200,
      render: (v, r) => (
        <DisciplineContractorPicker size="small" placeholder="—" aria-label="Row discipline"
          disciplines={companyDisciplines} roster={roster}
          value={v ? { discipline: v, responsibleCompanyId: r.responsibleCompanyId } : null}
          onChange={(pick) => setRows((rs) => rs.map((x) => (x.key === r.key
            ? { ...x, discipline: pick ? pick.discipline : "", responsibleCompanyId: pick ? (pick.responsibleCompanyId || (pick.discipline === x.discipline ? x.responsibleCompanyId : "")) : "" }
            : x)))} />
      ),
    },
    // Level / Area / Grid / System from the project's own lists (searchable),
    // or typed - the same picker as the single issue and the checklist, one
    // column each so each can be resized. System is kept on the row (it was
    // offered here before but never held, so a pick snapped back to empty).
    ...["level", "area", "grid", "system"].map((f) => ({
      title: f[0].toUpperCase() + f.slice(1), key: f, dataIndex: f, width: { level: 110, area: 170, grid: 150, system: 150 }[f],
      render: (_, r) => (
        <LocationPicker projectId={projectId} size="small" fill fields={[f]}
                        value={{ level: r.level, area: r.area, grid: r.grid, system: r.system }}
                        onChange={(loc) => setLoc(r.key, loc)} />
      ),
    })),
    {
      // Required (owner, 2026-10-16); the contractor cost page projects
      // cost from it.
      title: <span>Est. hours <span style={{ color: "#B84B0A" }}>*</span></span>, dataIndex: "estimatedHours", width: 90,
      render: (v, r) => (
        <InputNumber size="small" aria-label="Estimated hours" min={0.25} step={0.25} precision={2}
                     value={v} placeholder="hours" style={{ width: "100%" }}
                     status={r.title.trim() && !(Number(v) > 0) ? "error" : undefined}
                     onChange={(val) => setCell(r.key, "estimatedHours", val)} />
      ),
    },
    {
      title: "Priority", dataIndex: "priority", width: 100,
      render: (v, r) => (
        <Select size="small" value={v} style={{ width: "100%" }}
                options={PRIORITIES.map((p) => ({ value: p, label: p }))}
                onChange={(val) => setCell(r.key, "priority", val)} />
      ),
    },
    {
      title: "Due", dataIndex: "dueDate", width: 130,
      render: (v, r) => (
        <input type="date" value={v}
               style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "2px 6px", fontSize: 12, width: "100%" }}
               onChange={(e) => setCell(r.key, "dueDate", e.target.value)} />
      ),
    },
    {
      // Pick from everyone on the project - the owner company's own
      // people and every partner company's members and contacts - rather
      // than retyping an address. The values stay EMAILS, which is what
      // POST /issue/bulk resolves (and invites, when unknown), and there
      // can be more than one: an issue two trades have to fix together is
      // the normal case, not an edge case.
      title: "Responsible", dataIndex: "responsibleEmails", width: 260,
      render: (v, r) => (
        <Select
          size="small" allowClear showSearch style={{ width: "100%" }}
          // tags mode: an address that is not on the project can still be
          // typed, and the server invites them - same as before.
          mode="tags" tokenSeparators={[",", ";", " "]} maxTagCount="responsive"
          // A tag shows an email, and several tags in a 260px cell show
          // fragments of emails. Hovering names the person, their company
          // and whether they are being invited, and the x on the tag takes
          // one off - the row does not have to be cleared and retyped
          // because one address was picked by mistake.
          tagRender={({ value: email, closable, onClose }) => {
            const d = describePerson(email);
            return (
              <Tooltip
                placement="top"
                title={(
                  <span>
                    <b>{d.title}</b>
                    <br />
                    <span style={{ fontSize: 11 }}>{d.sub}</span>
                  </span>
                )}
              >
                <Tag
                  closable={closable}
                  onClose={onClose}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  style={{ marginInlineEnd: 4, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {d.title}
                </Tag>
              </Tooltip>
            );
          }}
          // The overflow chip lists everyone it is hiding, so "+3" is not
          // a number you have to widen the column to read.
          maxTagPlaceholder={(hidden) => (
            <Tooltip
              placement="top"
              title={(
                <div>
                  {hidden.map((h) => {
                    const d = describePerson(h.value);
                    return (
                      <div key={String(h.value)} style={{ marginBottom: 3 }}>
                        <b>{d.title}</b>
                        <br />
                        <span style={{ fontSize: 11 }}>{d.sub}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            >
              <span>+{hidden.length}</span>
            </Tooltip>
          )}
          value={v || []}
          placeholder={rosterLoading ? "loading people…" : "people, or type emails"}
          optionFilterProp="label"
          notFoundContent={rosterLoading ? "loading…" : "No match - type a full email to invite"}
          options={responsibleGroups}
          maxTagPlaceholder={peopleTagPlaceholder(responsibleGroups)}
          filterOption={filterPeopleOption} optionRender={renderPeopleOption}
          onChange={(vals) => setCell(
            r.key, "responsibleEmails",
            (vals || []).map((x) => String(x).trim().toLowerCase()).filter(Boolean)
          )}
        />
      ),
    },
    {
      title: "", key: "del", width: 40,
      render: (_, r) => (
        <Button size="small" type="text" icon={<DeleteOutlined />}
                onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} />
      ),
    },
  ];
  const colKey = (c) => c.key || c.dataIndex;
  const sizedColumns = columns.map((c) => {
    const w = widths[colKey(c)] || c.width;
    return {
      ...c, width: w,
      onHeaderCell: () => ({ width: w, onResize: (nw) => setWidths((ws) => ({ ...ws, [colKey(c)]: nw })) }),
    };
  });
  const tableWidth = 36 + sizedColumns.reduce((n, c) => n + (Number(c.width) || 0), 0);

  return (
    <div style={{ padding: 24, fontFamily: "Inter, system-ui, sans-serif", margin: "0 auto" }}>
      <CoordinationBreadcrumbs
        items={[{ label: "Issues Manager", to: "/issues" }]}
        current="Batch entry"
      />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Add multiple issues</h2>
        <Select filterOption={matchOption} style={{ width: 260 }} value={projectId || undefined}
                placeholder="Project" showSearch optionFilterProp="label"
                options={projects.map((p) => ({ value: p._id, label: p.title || p.name }))}
                onChange={setProjectId} />
        <span style={{ flex: 1 }} />
        <Button icon={<PlusOutlined />} onClick={() => setRows((rs) => [...rs, emptyRow()])}>
          Add row
        </Button>
        <Popconfirm title={`Create ${rows.filter((r) => r.title.trim()).length} issue(s)?`} onConfirm={submit}>
          <Button type="primary" icon={<SendOutlined />} loading={saving} disabled={!projectId || !mayCreate}
                  title={mayCreate ? undefined : "Creating issues needs the issueCreate permission"}>
            Create issues
          </Button>
        </Popconfirm>
        <Button onClick={() => history.push(projectId ? `/issues?projectId=${projectId}` : "/issues")}>Back to Issues Manager</Button>
      </div>
      <FillPanel
        fill={fill} setFill={setFill} reset={() => setFill(emptyFill)} applyFill={applyFill}
        selectedCount={selectedKeys.length} projectId={projectId}
        responsibleGroups={responsibleGroups} rosterLoading={rosterLoading} describePerson={describePerson}
        tagOptions={tagOptions} planOptions={planOptions} companyDisciplines={companyDisciplines} roster={roster}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap", fontSize: 12 }}>
        <b>Require proof to mark resolved (all rows):</b>
        <Checkbox.Group value={proof} onChange={setProof}
          options={[
            { label: "Screenshot of the progress", value: "screenshot" },
            { label: "Uploaded document (PDF / Excel / CSV)", value: "document" },
            { label: "Model push / diff from Revit or Navisworks", value: "modelPush" },
            { label: "Resolution pushed from the Revit / Navisworks plugin", value: "pluginResolve" },
          ]} />
      </div>

      <Alert type="info" showIcon style={{ marginBottom: 12 }}
             message="Paste a screenshot onto a row to attach it to THAT issue (click the row first, or paste with the pointer over it). Paste CSV rows anywhere on this page to append them (columns: Title, Description, Activity, Discipline, Area, Level, Grid, Priority, Due, Responsible — header row optional; with a header, System, Tag and Estimated hours columns are read too). Drag a column header's right edge to resize it. A row can name several responsibles, separated by a comma or a semicolon; unknown addresses receive a registration invite." />

      <Table rowKey="key" dataSource={rows} columns={sizedColumns}
             size="small" pagination={false}
             tableLayout="fixed" scroll={{ x: tableWidth }}
             components={{ header: { cell: ResizableTitle } }}
             rowSelection={{ selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys), columnWidth: 36 }}
             onRow={(r) => ({
               onMouseEnter: () => setHoverRowKey(r.key),
               onMouseLeave: () => setHoverRowKey((k) => (k === r.key ? "" : k)),
               // The caret decides, so record it - and let go when it
               // leaves, or the last row clicked would keep the paste
               // forever.
               onFocus: () => setFocusRowKey(r.key),
               onBlur: () => setFocusRowKey((k) => (k === r.key ? "" : k)),
               style: r.key === armedRowKey
                 ? { background: "#F0F7FF", boxShadow: "inset 3px 0 0 #2F6FED" }
                 : undefined,
             })}
             locale={{ emptyText: <Empty description="No rows — add or paste" /> }} />

      {result && (
        <div style={{ marginTop: 16 }}>
          {result.created?.length > 0 && (
            <Alert
              type="success" showIcon style={{ marginBottom: 8 }}
              message={`Created: ${result.created.map((c) => `#${c.displayId}`).join(", ")}`}
              description="They are in the Issues Manager under this project, newest first."
              action={
                // Carry the project, or the list opens on whichever
                // project it happened to remember and they look missing.
                <Button size="small" type="primary"
                        onClick={() => history.push(`/issues?projectId=${projectId}`)}>
                  View these issues
                </Button>
              }
            />
          )}
          {result.invited?.length > 0 && (
            <Alert type="info" showIcon style={{ marginBottom: 8 }}
                   message={`Invites sent: ${result.invited.map((i) => `${i.email} (${i.count})${i.emailSent === false ? " — email failed, copy link from console" : ""}`).join(", ")}`} />
          )}
          {result.errors?.length > 0 && (
            <Alert type="warning" showIcon
                   message={`Rows with errors (kept in the table): ${result.errors.map((e) => `row ${e.row + 1}: ${e.err}`).join("; ")}`} />
          )}
        </div>
      )}
      {result?.invited?.some((i) => i.emailSent === false) &&
        console.log("registration links:", result.invited.filter((i) => i.registrationUrl))}
      {rows.length > 0 && (
        <Tag style={{ marginTop: 12 }} color="default">
          {rows.filter((r) => r.title.trim()).length} row(s) ready
        </Tag>
      )}
    </div>
  );
}
